import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import {
  adminApi,
  type Agent,
  type AgentGender,
  type AgentRole,
  type AgentSortField,
  type AgentType,
  type SortDir,
} from '../api/admin';
import { useAgentRoles } from '../lib/use-agent-roles';
import { DEFAULT_TEAM_ICON, teamLabel } from '../lib/team-icon';

// 性別の表示ラベル（人格表現の一要素）。
const genderOptions: { value: AgentGender; label: string }[] = [
  { value: 'unset', label: '未設定' },
  { value: 'male', label: '男性' },
  { value: 'female', label: '女性' },
  { value: 'neutral', label: '中性' },
];
const genderLabel = (g: AgentGender) => genderOptions.find((o) => o.value === g)?.label ?? g;

// タイプ（区分）の表示ラベル。役割(role)の母集合を決める軸（ADR0006）。
const typeOptions: { value: AgentType; label: string }[] = [
  { value: 'dev', label: '開発系' },
  { value: 'company', label: '会社系' },
];
const typeLabel = (t: AgentType) => typeOptions.find((o) => o.value === t)?.label ?? t;

// タイプごとに割り当てうる責務(役割)。type を選ぶとここで絞り込まれる。
const rolesByType: Record<AgentType, { value: AgentRole; label: string }[]> = {
  dev: [
    { value: 'pm_se', label: 'PM兼SE' },
    { value: 'implementer', label: '実装者' },
    { value: 'qa_tester', label: 'テスターQA' },
    { value: 'git_env_manager', label: 'GIT環境構築マネージャ' },
  ],
  company: [
    { value: 'coo', label: 'COO' },
    { value: 'dev_lead', label: '開発責任者' },
    { value: 'finance_lead', label: '財務責任者' },
  ],
};
// 役割の表示ラベル（全タイプ横断で検索）。'unset' は未設定。
const roleLabel = (r: AgentRole) => {
  if (r === 'unset') return '未設定';
  for (const list of Object.values(rolesByType)) {
    const hit = list.find((o) => o.value === r);
    if (hit) return hit.label;
  }
  return r;
};

// #178: 検索条件の永続化キー。条件のスキーマを変えたら v を上げて旧データを無視する。
const SEARCH_STORAGE_KEY = 'konecco.agents.search.v1';
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

interface AgentSearchState {
  q: string; // 名前/愛称のあいまい検索
  type: string; // 区分コードで絞り込み（'' は指定なし。#176 マスタのコード）
  role: string; // 責務コードで絞り込み（'' は指定なし）
  sortBy: AgentSortField;
  sortDir: SortDir;
  page: number; // 1 始まり
  pageSize: number;
}

const DEFAULT_SEARCH: AgentSearchState = {
  q: '',
  type: '',
  role: '',
  sortBy: 'id',
  sortDir: 'asc',
  page: 1,
  pageSize: 20,
};

// localStorage から検索条件を復元。壊れた値や旧スキーマは既定で補完する。
function loadSearch(): AgentSearchState {
  try {
    const raw = localStorage.getItem(SEARCH_STORAGE_KEY);
    if (!raw) return DEFAULT_SEARCH;
    return { ...DEFAULT_SEARCH, ...(JSON.parse(raw) as Partial<AgentSearchState>) };
  } catch {
    return DEFAULT_SEARCH;
  }
}

// エージェント（旧称: 擬人 / persona）= 独立した人格。プロジェクトに縛られず、
// あとから別プロジェクトに割り当て・異動できる「会話相手」。名前・性別・アイコン・性格・割り当て先を登録。
export function AgentsPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['agents'] });

  // #178: 検索条件は localStorage に保存し、次回開いたときに復元して初期検索する。
  const [search, setSearch] = useState<AgentSearchState>(loadSearch);
  // 入力途中はリクエストを飛ばさず、確定（Enter / 検索ボタン）で search.q へ反映する。
  const [qDraft, setQDraft] = useState(search.q);

  // 条件が変わるたびに保存。次回マウント時に loadSearch() が拾う。
  useEffect(() => {
    try {
      localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(search));
    } catch {
      /* localStorage 不可環境は黙ってスキップ */
    }
  }, [search]);

  // 条件を部分更新するヘルパ。検索/絞り込み/並び替え/件数を変えたらページは 1 に戻す。
  const patchSearch = (patch: Partial<AgentSearchState>, resetPage = true) =>
    setSearch((s) => ({ ...s, ...patch, ...(resetPage ? { page: 1 } : {}) }));

  // #176 マスタ: 区分(type)/責務(role) の候補。検索フォームのドロップダウンはここから出す（カスケード）。
  const mastersQuery = useQuery({ queryKey: ['agent-masters'], queryFn: adminApi.listAgentMasters });
  const masterTypes = mastersQuery.data?.types ?? [];
  // 表示ラベルもマスタ優先（未取得時は固定ラベルにフォールバック）。
  const typeLabelOf = (code: AgentType) =>
    masterTypes.find((t) => t.code === code)?.label ?? typeLabel(code);
  const roleLabelOf = (code: AgentRole) => {
    if (code === 'unset') return '未設定';
    for (const t of masterTypes) {
      const hit = t.roles.find((r) => r.code === code);
      if (hit) return hit.label;
    }
    return roleLabel(code);
  };
  // 責務の候補: 区分選択時はその配下のみ（カスケード）、未選択なら全区分を平坦化。
  const roleChoices = search.type
    ? (masterTypes.find((t) => t.code === search.type)?.roles ?? [])
    : masterTypes.flatMap((t) => t.roles);

  // #178: サーバ問い合わせ。queryKey に条件を含めるので、条件が変われば自動で再取得される。
  // 全件取得は行わず、検索/ソート/ページングは常にサーバ側。
  const agentsQuery = useQuery({
    queryKey: ['agents', search],
    queryFn: () =>
      adminApi.listAgents({
        q: search.q || undefined,
        type: search.type || undefined,
        role: search.role || undefined,
        sortBy: search.sortBy,
        sortDir: search.sortDir,
        page: search.page,
        pageSize: search.pageSize,
      }),
    placeholderData: keepPreviousData, // ページ送り時に前ページを保持して点滅を防ぐ
  });
  const result = agentsQuery.data;
  const total = result?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / search.pageSize));
  const hasFilter = Boolean(search.q || search.type || search.role);

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Agent | null>(null);

  const createAgent = useMutation({
    mutationFn: adminApi.createAgent,
    onSuccess: () => {
      setCreateOpen(false);
      invalidate();
    },
  });
  const updateAgent = useMutation({
    mutationFn: (vars: {
      id: number;
      input: {
        name?: string;
        nickname?: string | null;
        type?: AgentType;
        role?: AgentRole;
        gender?: AgentGender;
        icon?: string | null;
        personality?: string | null;
      };
    }) => adminApi.updateAgent(vars.id, vars.input),
    onSuccess: () => {
      setEditTarget(null);
      invalidate();
    },
  });
  const deleteAgent = useMutation({ mutationFn: adminApi.deleteAgent, onSuccess: invalidate });

  // ソート列ヘッダ。クリックで昇順/降順トグル（別列なら昇順から）。
  const toggleSort = (field: AgentSortField) =>
    patchSearch(
      search.sortBy === field
        ? { sortDir: search.sortDir === 'asc' ? 'desc' : 'asc' }
        : { sortBy: field, sortDir: 'asc' },
    );
  const SortableTh = ({ field, children }: { field: AgentSortField; children: ReactNode }) => (
    <th
      style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
      onClick={() => toggleSort(field)}
      title="クリックで並び替え"
    >
      {children}
      <span style={{ color: '#2563eb' }}>
        {search.sortBy === field ? (search.sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
      </span>
    </th>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="k-card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <h2 style={{ margin: 0 }}>エージェント</h2>
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        </div>
        <p className="k-muted" style={{ marginTop: 0, fontSize: 13 }}>
          性格を持つ独立した「会話相手」。プロジェクトに縛られず、あとから別プロジェクトへ割り当て・異動できます。
        </p>

        {/* #178 検索フォーム: q(名前/愛称あいまい) + 区分/責務(マスタ・カスケード)。すべてサーバ側で処理。 */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            patchSearch({ q: qDraft.trim() });
          }}
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'flex-end',
            marginBottom: 12,
          }}
        >
          <div style={searchField}>
            <label style={searchLabel}>あいまい検索（名前・愛称）</label>
            <input
              placeholder="名前 / 愛称で検索"
              value={qDraft}
              onChange={(e) => setQDraft(e.target.value)}
              style={{ minWidth: 200 }}
            />
          </div>
          <div style={searchField}>
            <label style={searchLabel}>区分</label>
            <select
              value={search.type}
              onChange={(e) => {
                const nextType = e.target.value;
                // 区分を変えたら、その配下に無い責務はクリア（カスケード整合）。
                const roleStillValid =
                  !search.role ||
                  (nextType
                    ? (masterTypes.find((t) => t.code === nextType)?.roles ?? []).some(
                        (r) => r.code === search.role,
                      )
                    : true);
                patchSearch({ type: nextType, role: roleStillValid ? search.role : '' });
              }}
            >
              <option value="">すべて</option>
              {masterTypes.map((t) => (
                <option key={t.id} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>
          <div style={searchField}>
            <label style={searchLabel}>責務{search.type ? '（区分で絞り込み）' : ''}</label>
            <select
              value={search.role}
              onChange={(e) => patchSearch({ role: e.target.value })}
            >
              <option value="">すべて</option>
              {roleChoices.map((r) => (
                <option key={r.id} value={r.code}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <button type="submit">検索</button>
          {hasFilter && (
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                setQDraft('');
                patchSearch({ q: '', type: '', role: '' });
              }}
            >
              クリア
            </button>
          )}
        </form>

        {agentsQuery.isLoading && <p>Loading…</p>}
        {agentsQuery.error && (
          <p className="k-error">取得に失敗しました: {(agentsQuery.error as Error).message}</p>
        )}
        {result &&
          (result.items.length === 0 ? (
            <p className="k-muted">
              {hasFilter
                ? '条件に一致するエージェントがいません。検索条件を変えてみてください。'
                : 'まだエージェントがいません。右上の「＋ 新規作成」から追加してください。'}
            </p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                  <SortableTh field="id">id</SortableTh>
                  <SortableTh field="name">名前 / 愛称</SortableTh>
                  <SortableTh field="type">区分</SortableTh>
                  <SortableTh field="role">責務</SortableTh>
                  <SortableTh field="gender">性別</SortableTh>
                  <th style={th}>所属チーム</th>
                  <th style={th}>性格</th>
                  <th style={th}>操作</th>
                </tr>
              </thead>
              <tbody>
                {result.items.map((a) => (
                  <tr key={a.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={td}>{a.id}</td>
                    <td style={td}>
                      <span style={{ marginRight: 6 }}>{a.icon || '🤖'}</span>
                      <strong>{a.nickname || a.name}</strong>
                      {a.nickname && (
                        <span className="k-muted" style={{ marginLeft: 6, fontSize: 12 }}>
                          （{a.name}）
                        </span>
                      )}
                    </td>
                    <td style={td}>{typeLabelOf(a.type)}</td>
                    <td style={td}>
                      {a.role === 'unset' ? (
                        <span className="k-muted">未設定</span>
                      ) : (
                        roleLabelOf(a.role)
                      )}
                    </td>
                    <td style={td}>{genderLabel(a.gender)}</td>
                    <td style={td}>
                      {a.teams.length === 0 ? (
                        <span className="k-muted">未所属</span>
                      ) : (
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {a.teams.map((t) => (
                            <span key={t.id} className="k-badge">
                              <span style={{ marginRight: 4 }}>{t.icon || DEFAULT_TEAM_ICON}</span>
                              {t.name}
                            </span>
                          ))}
                        </span>
                      )}
                    </td>
                    <td style={{ ...td, maxWidth: 240, color: '#475569' }}>
                      {a.personality ? (
                        <span title={a.personality}>
                          {a.personality.length > 40
                            ? `${a.personality.slice(0, 40)}…`
                            : a.personality}
                        </span>
                      ) : (
                        <span className="k-muted">—</span>
                      )}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn-ghost" type="button" onClick={() => setEditTarget(a)}>
                          変更
                        </button>
                        <button
                          className="btn-danger"
                          type="button"
                          disabled={deleteAgent.isPending}
                          onClick={() => {
                            if (confirm(`エージェント「${a.name}」を削除しますか？`)) {
                              deleteAgent.mutate(a.id);
                            }
                          }}
                        >
                          削除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ))}

        {/* #178 ページャ: 表示件数(pageSize) + ページ番号(page) を指定。 */}
        {result && total > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 12,
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 12,
            }}
          >
            <span className="k-muted" style={{ fontSize: 13 }}>
              全 {total} 件中 {(search.page - 1) * search.pageSize + 1}–
              {Math.min(search.page * search.pageSize, total)} 件を表示
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <label style={{ fontSize: 13 }}>
                表示件数{' '}
                <select
                  value={search.pageSize}
                  onChange={(e) => patchSearch({ pageSize: Number(e.target.value) })}
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="btn-ghost"
                disabled={search.page <= 1}
                onClick={() => patchSearch({ page: search.page - 1 }, false)}
              >
                ← 前
              </button>
              <span style={{ fontSize: 13 }}>
                {search.page} / {totalPages}
              </span>
              <button
                type="button"
                className="btn-ghost"
                disabled={search.page >= totalPages}
                onClick={() => patchSearch({ page: search.page + 1 }, false)}
              >
                次 →
              </button>
            </div>
          </div>
        )}

        {deleteAgent.error && (
          <p className="k-error">削除に失敗しました: {(deleteAgent.error as Error).message}</p>
        )}
      </section>

      {createOpen && (
        <AgentFormDialog
          mode="create"
          submitting={createAgent.isPending}
          error={createAgent.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(v) => createAgent.mutate(v)}
        />
      )}
      {editTarget && (
        <AgentFormDialog
          mode="edit"
          initial={editTarget}
          submitting={updateAgent.isPending}
          error={updateAgent.error as Error | null}
          onClose={() => setEditTarget(null)}
          onSubmit={(v) => updateAgent.mutate({ id: editTarget.id, input: v })}
        />
      )}
    </div>
  );
}

interface AgentFormValues {
  name: string;
  nickname: string | null;
  type: AgentType;
  role: AgentRole;
  gender: AgentGender;
  icon: string | null;
  personality: string | null;
}

function AgentFormDialog({
  mode,
  initial,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  mode: 'create' | 'edit';
  initial?: Agent;
  onSubmit: (v: AgentFormValues) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  // 区分・役割の選択肢はマスタ管理(#176)から（静的 typeOptions/rolesByType ではなく登録内容に追従）。
  const { typeOptions, roleOptionsByType } = useAgentRoles();
  const [name, setName] = useState(initial?.name ?? '');
  const [nickname, setNickname] = useState(initial?.nickname ?? '');
  const [type, setType] = useState<AgentType>(initial?.type ?? 'dev');
  const [role, setRole] = useState<AgentRole>(initial?.role ?? 'unset');
  const [gender, setGender] = useState<AgentGender>(initial?.gender ?? 'unset');
  // 区分は新規時マスタ先頭を既定に（マスタ非同期取得のため空時はフォールバック）。
  const effectiveType = type || typeOptions[0]?.value || 'dev';
  const roleChoices = roleOptionsByType(effectiveType);
  // タイプ変更時：選択中の役割が新タイプに無ければ未設定へ戻す（type↔role 整合）。
  const onTypeChange = (next: AgentType) => {
    setType(next);
    if (role !== 'unset' && !roleOptionsByType(next).some((o) => o.value === role)) {
      setRole('unset');
    }
  };
  const [icon, setIcon] = useState(initial?.icon ?? '');
  const [personality, setPersonality] = useState(initial?.personality ?? '');
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;

  return (
    <Modal
      title={mode === 'create' ? '新しいエージェントを登録' : `エージェントを変更 — #${initial?.id}`}
      onClose={onClose}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          onSubmit({
            name: name.trim(),
            nickname: nickname.trim() || null,
            type: effectiveType,
            role,
            gender,
            icon: icon.trim() || null,
            personality: personality.trim() || null,
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ ...fieldWrap, width: 88 }}>
            <label style={labelStyle}>アイコン</label>
            <input
              placeholder="🤖"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              style={{ textAlign: 'center', fontSize: 20 }}
              maxLength={4}
            />
          </div>
          <div style={{ ...fieldWrap, flex: 1 }}>
            <label style={labelStyle}>名前（例: 田中）</label>
            <input
              placeholder="例: 田中"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>愛称（任意・例: 田中くん）— あれば優先表示</label>
          <input
            placeholder="例: 田中くん"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
          />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ ...fieldWrap, flex: 1 }}>
            <label style={labelStyle}>区分（タイプ）</label>
            <select
              value={effectiveType}
              disabled={typeOptions.length === 0}
              onChange={(e) => onTypeChange(e.target.value as AgentType)}
            >
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ ...fieldWrap, flex: 1 }}>
            <label style={labelStyle}>責務（役割）— 区分で絞り込み</label>
            <select value={role} onChange={(e) => setRole(e.target.value as AgentRole)}>
              <option value="unset">未設定</option>
              {roleChoices.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>性別</label>
          <select value={gender} onChange={(e) => setGender(e.target.value as AgentGender)}>
            {genderOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>所属チーム</label>
          <p className="k-muted" style={{ fontSize: 12, margin: 0 }}>
            所属は「チーム」画面で役割枠にこのエージェントを割り当てると決まります（ここでは設定しません）。
            {initial && initial.teams.length > 0 ? (
              <>
                {' '}現在の所属:{' '}
                {initial.teams.map((t) => teamLabel(t)).join('、 ')}
              </>
            ) : (
              <>{' '}現在は未所属です。</>
            )}
          </p>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>性格・口調（人格プロンプト・任意）</label>
          <textarea
            placeholder="例: 丁寧で慎重。語尾は「〜ですね」。専門用語は噛み砕いて説明する。"
            value={personality}
            onChange={(e) => setPersonality(e.target.value)}
            rows={4}
          />
        </div>
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !name.trim()}>
            {mode === 'create' ? '登録' : '変更を保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
  width = 460,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const th = { padding: '8px 12px', fontWeight: 600, fontSize: 13 } as const;
const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
// #178 検索フォームの各フィールド（ラベル＋入力を縦積み）。
const searchField = { display: 'flex', flexDirection: 'column', gap: 4 } as const;
const searchLabel = { fontSize: 12, fontWeight: 600, color: '#475569' } as const;
